import { useEffect, useRef } from 'react';
import type { Branding, IntroScreen as IntroScreenData } from '@branchworks/shared';
import { readableForeground } from './branding';

/**
 * The optional title screen, shown before the first scene when the scenario
 * carries an `intro`. Pure chrome — entering it has not started the session;
 * pressing the button does (the App restarts the playthrough so session_start
 * fires at the real start moment).
 *
 * Accessibility: it's the page's <main> and <h1>; focus lands on the heading on
 * mount so a screen reader announces the title; the action button is the only
 * other focusable element and inherits the global cream focus ring (placed
 * against the screen background via outline-offset, so it stays visible even on
 * a coloured brand background).
 */
export function TitleScreen({
  intro,
  branding,
  onEnter,
}: {
  intro: IntroScreenData;
  branding?: Branding;
  onEnter: () => void;
}) {
  const headingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  const { bg, fg, faint } = readableForeground(branding?.backgroundColor);

  return (
    <main
      aria-label="Title screen"
      className="min-h-screen flex items-center justify-center p-8"
      style={{ background: bg, color: fg }}
    >
      <div className="max-w-2xl w-full text-center">
        {branding?.logoUrl && (
          <img
            src={branding.logoUrl}
            alt=""
            className="mx-auto mb-8 max-h-28 w-auto"
            onError={(e) => ((e.target as HTMLImageElement).style.display = 'none')}
          />
        )}
        <h1
          ref={headingRef}
          tabIndex={-1}
          className="text-4xl font-bold leading-tight mb-6 focus:outline-none"
        >
          {intro.heading}
        </h1>
        {intro.body && (
          <p className="text-lg leading-relaxed mb-10 mx-auto max-w-xl" style={{ color: faint }}>
            {intro.body}
          </p>
        )}
        <button
          onClick={onEnter}
          className="px-8 py-3 rounded text-base font-semibold tracking-wide"
          style={{ background: fg, color: bg }}
        >
          {intro.buttonLabel || 'Begin'}
        </button>
      </div>
    </main>
  );
}
